import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AdminSidebar from '../components/AdminSidebar';
import LoginForm from '../components/LoginForm';
import useIcalUnavailableRanges from '../hooks/useIcalUnavailableRanges';
import i18n from '../i18n';
import { clearAuth, getAuthRole, setAuthRole, subscribeAuthRole } from '../utils/auth';
import './GestionePrenotazioni.css';

const normalizeDate = (date) => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const toYmd = (date) => {
  const d = normalizeDate(date);
  return d.toISOString().slice(0, 10);
};

const parseYmdToDate = (ymd) => {
  if (typeof ymd !== 'string' || !ymd) {
    return null;
  }
  // Évite les soucis de fuseau horaire.
  const date = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const msPerDay = 24 * 60 * 60 * 1000;

const diffDays = (start, end) => {
  if (!(start instanceof Date) || !(end instanceof Date)) {
    return 0;
  }
  const days = Math.round((normalizeDate(end) - normalizeDate(start)) / msPerDay);
  return Math.max(0, days);
};

const reservationKeyForRange = (range) => {
  if (!range?.start || !range?.end) {
    return '';
  }
  return `${toYmd(range.start)}_${toYmd(range.end)}`;
};

const BOOKING_MODES = [
  { value: '', label: '—' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking', label: 'Booking' },
  { value: 'vrbo', label: 'Vrbo' },
  { value: 'subito', label: 'Subito' },
  { value: 'direct', label: 'Diretto' },
  { value: 'sejour_proprietaire', label: 'Soggiorno proprietario' },
  { value: 'errore', label: 'Errore' },
];

const bookingModeLabelForValue = (value) => {
  const found = BOOKING_MODES.find((mode) => mode.value === value);
  return found ? found.label : '—';
};

// Chaque pays est cherchable en italien (valeur stockée) ET en français.
const COUNTRY_OPTIONS = [
  { it: 'Italia', fr: 'Italie' },
  { it: 'Francia', fr: 'France' },
  { it: 'Germania', fr: 'Allemagne' },
  { it: 'Spagna', fr: 'Espagne' },
  { it: 'Portogallo', fr: 'Portugal' },
  { it: 'Regno Unito', fr: 'Royaume-Uni' },
  { it: 'Irlanda', fr: 'Irlande' },
  { it: 'Svizzera', fr: 'Suisse' },
  { it: 'Belgio', fr: 'Belgique' },
  { it: 'Paesi Bassi', fr: 'Pays-Bas' },
  { it: 'Austria', fr: 'Autriche' },
  { it: 'Svezia', fr: 'Suède' },
  { it: 'Norvegia', fr: 'Norvège' },
  { it: 'Danimarca', fr: 'Danemark' },
  { it: 'Finlandia', fr: 'Finlande' },
  { it: 'Polonia', fr: 'Pologne' },
  { it: 'Repubblica Ceca', fr: 'Rép. tchèque' },
  { it: 'Ungheria', fr: 'Hongrie' },
  { it: 'Romania', fr: 'Roumanie' },
  { it: 'Bulgaria', fr: 'Bulgarie' },
  { it: 'Grecia', fr: 'Grèce' },
  { it: 'Stati Uniti', fr: 'États-Unis' },
  { it: 'Canada', fr: 'Canada' },
  { it: 'Brasile', fr: 'Brésil' },
  { it: 'Argentina', fr: 'Argentine' },
  { it: 'Australia', fr: 'Australie' },
  { it: 'Nuova Zelanda', fr: 'Nouvelle-Zélande' },
  { it: 'Russia', fr: 'Russie' },
  { it: 'Ucraina', fr: 'Ukraine' },
];

const stripAccents = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

// Champ pays avec autocomplétion : filtre à la fois sur le nom italien et le nom
// français, mais enregistre toujours le nom italien (cohérence des statistiques).
const CountryAutocomplete = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const suggestions = useMemo(() => {
    const query = stripAccents(value);
    if (!query) {
      return COUNTRY_OPTIONS;
    }
    return COUNTRY_OPTIONS.filter(
      (country) => stripAccents(country.it).includes(query) || stripAccents(country.fr).includes(query)
    );
  }, [value]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  return (
    <div className="prenotazioni-country" ref={wrapRef}>
      <input
        type="text"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Cerca (it / fr)…"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="prenotazioni-country-list">
          {suggestions.map((country) => (
            <li key={country.it}>
              <button
                type="button"
                onClick={() => {
                  onChange(country.it);
                  setOpen(false);
                }}
              >
                <span className="prenotazioni-country-it">{country.it}</span>
                {country.fr !== country.it && (
                  <span className="prenotazioni-country-fr">{country.fr}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BED_LABELS = {
  matrimoniale: '🛏️ Matrimoniale',
  lettino1: '🛏️ Lettino 1',
  lettino2: '🛏️ Lettino 2',
  divanoLetto: '🛋️ Divano letto',
  lettiniAssemblati: '🛏️ Lettini assemblati',
};

const defaultBeds = {
  matrimoniale: false,
  lettino1: false,
  lettino2: false,
  divanoLetto: false,
  lettiniAssemblati: false,
};

const overlayToForm = (overlay) => {
  if (!overlay || typeof overlay !== 'object') {
    return {
      name: '',
      adults: 0,
      children: 0,
      beds: { ...defaultBeds },
      manualSlot: false,
      bookingMode: '',
      bookingDate: '',
      country: '',
      totalNet: 0,
      totalGross: 0,
      commission: 0,
      taxPaid: 0,
      pricePerNight: 0,
      extraPerson: 0,
      notes: '',
      comments: [],
    };
  }

  return {
    name: overlay.name || '',
    adults: Number.isFinite(overlay.adults) ? overlay.adults : 0,
    children: Number.isFinite(overlay.children) ? overlay.children : 0,
    beds: { ...defaultBeds, ...(overlay.beds || {}) },
    manualSlot: Boolean(overlay.manualSlot),
    bookingMode: overlay.bookingMode || '',
    bookingDate: typeof overlay.bookingDate === 'string' ? overlay.bookingDate : '',
    country: typeof overlay.country === 'string' ? overlay.country : '',
    totalNet: Number.isFinite(overlay.totalNet) ? overlay.totalNet : 0,
    totalGross: Number.isFinite(overlay.totalGross) ? overlay.totalGross : 0,
    commission: Number.isFinite(overlay.commission) ? overlay.commission : 0,
    taxPaid: Number.isFinite(overlay.taxPaid) ? overlay.taxPaid : 0,
    pricePerNight: Number.isFinite(overlay.pricePerNight) ? overlay.pricePerNight : 0,
    extraPerson: Number.isFinite(overlay.extraPerson) ? overlay.extraPerson : 0,
    notes: overlay.notes || '',
    comments: Array.isArray(overlay.comments) ? overlay.comments : [],
  };
};

const defaultFormForRange = (range) => {
  return {
    name: '',
    adults: 0,
    children: 0,
    beds: { ...defaultBeds },
    manualSlot: Boolean(range?.manualSlot),
    bookingMode: getDefaultBookingMode(range),
    bookingDate: '',
    country: '',
    totalNet: 0,
    totalGross: 0,
    commission: 0,
    taxPaid: 0,
    pricePerNight: 0,
    extraPerson: 0,
    notes: '',
    comments: [],
  };
};

const parseReservationKey = (key) => {
  if (typeof key !== 'string') {
    return null;
  }
  const trimmed = key.trim();
  if (!/^\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }
  const [from, to] = trimmed.split('_');
  const start = parseYmdToDate(from);
  const end = parseYmdToDate(to);
  if (!(start instanceof Date) || !(end instanceof Date)) {
    return null;
  }
  return { start: normalizeDate(start), end: normalizeDate(end) };
};

// Construit une Date telle que toYmd(date) === ymd, quel que soit le fuseau.
// Indispensable pour matérialiser un overlay : la ligne doit ré-encoder vers sa
// clé JSON exacte (sinon la recherche de l'overlay échoue à cause du décalage
// UTC de toYmd) et afficher la même date que la ligne iCal correspondante.
const dateForKeyPart = (ymd) => {
  const base = parseYmdToDate(ymd);
  if (!base) {
    return null;
  }
  let d = normalizeDate(base);
  if (toYmd(d) !== ymd) {
    d = normalizeDate(new Date(base.getTime() + msPerDay));
    if (toYmd(d) !== ymd) {
      d = normalizeDate(new Date(base.getTime() - msPerDay));
    }
  }
  return d;
};

// Un overlay « vide » (aucun nom, aucun montant, aucun commentaire, non manuel)
// ne doit pas encombrer la liste des périodes.
const overlayHasContent = (overlay) => {
  if (!overlay || typeof overlay !== 'object') {
    return false;
  }
  if (typeof overlay.name === 'string' && overlay.name.trim()) {
    return true;
  }
  if (Number(overlay.totalNet) > 0 || Number(overlay.totalGross) > 0) {
    return true;
  }
  if (Array.isArray(overlay.comments) && overlay.comments.length > 0) {
    return true;
  }
  return Boolean(overlay.manualSlot);
};

const overlayRichness = (overlay) => {
  let score = 0;
  if (typeof overlay?.name === 'string' && overlay.name.trim()) {
    score += 10;
  }
  for (const field of ['totalNet', 'totalGross', 'commission', 'taxPaid', 'pricePerNight']) {
    if (Number(overlay?.[field]) > 0) {
      score += 2;
    }
  }
  if (Array.isArray(overlay?.comments)) {
    score += overlay.comments.length * 3;
  }
  return score;
};

const dayDiff = (a, b) => Math.round((normalizeDate(a) - normalizeDate(b)) / msPerDay);

// Deux périodes se chevauchent (bornes demi-ouvertes [start, end[ : un départ et
// une arrivée le même jour NE se chevauchent pas — l'enchaînement jour J est permis).
const rangesOverlap = (a, b) =>
  normalizeDate(a.start) < normalizeDate(b.end) && normalizeDate(b.start) < normalizeDate(a.end);

const isSameForm = (left, right) => {
  // Suffisant ici (objets simples) + évite d'ajouter une dépendance.
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch (_) {
    return false;
  }
};

const getDefaultBookingMode = (range) => {
  if (range?.manualSlot) {
    return 'direct';
  }
  const sourceId = typeof range?.sourceId === 'string' ? range.sourceId : '';
  if (['airbnb', 'booking'].includes(sourceId)) {
    return sourceId;
  }
  return '';
};

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('it-IT', {
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('it-IT', {
  style: 'percent',
  maximumFractionDigits: 1,
});

const CHART_GREEN = '#1f9d55';

// Libellé de camembert : pourcentage de la part.
const renderPiePercent = ({ percent }) =>
  percent > 0 ? percentFormatter.format(percent) : '';

const roundTo2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const deriveCommissionAmount = ({ totalNet, totalGross, commission, taxPaid }) => {
  const lordo = Number.isFinite(totalNet) ? totalNet : 0;
  const netto = Number.isFinite(totalGross) ? totalGross : 0;
  const tax = Number.isFinite(taxPaid) && taxPaid > 0 ? taxPaid : 0;
  const manualCommission = Number.isFinite(commission) ? commission : 0;

  if (manualCommission > 0) {
    return manualCommission;
  }

  const inferred = lordo - netto - tax;
  return inferred > 0 ? inferred : 0;
};

const deriveRealIncome = ({ totalNet, totalGross, commission, taxPaid }) => {
  const lordo = Number.isFinite(totalNet) ? totalNet : 0;
  const tax = Number.isFinite(taxPaid) && taxPaid > 0 ? taxPaid : 0;
  const commissionAmount = deriveCommissionAmount({ totalNet, totalGross, commission, taxPaid });
  const inferred = lordo - commissionAmount - tax;

  if (inferred > 0) {
    return inferred;
  }

  const netto = Number.isFinite(totalGross) ? totalGross : 0;
  return netto > 0 ? netto : 0;
};

// totalNet stocké = lordo (prix payé par le locataire, le plus élevé)
// totalGross stocké = netto (ce qui nous reste après commission)
// lordo = netto + commission
const computeRates = ({ totalGross, totalNet, taxPaid }) => {
  const lordo = Number.isFinite(totalNet) ? totalNet : 0;
  const netto = Number.isFinite(totalGross) ? totalGross : 0;
  const tax = Number.isFinite(taxPaid) ? taxPaid : 0;

  if (lordo <= 0) {
    return { commissionRate: null, taxRate: null };
  }

  const taxRate = tax > 0 ? Math.min(1, Math.max(0, tax / lordo)) : 0;
  const commissionAmount = Math.max(0, lordo - netto);
  const commissionRate = Math.min(1, Math.max(0, commissionAmount / lordo));

  return { commissionRate, taxRate };
};

const monthKeyForDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const monthLabelForKey = (key) => {
  const match = /^\d{4}-\d{2}$/.test(key);
  if (!match) {
    return key;
  }
  const date = new Date(`${key}-01T00:00:00`);
  return new Intl.DateTimeFormat('it-IT', { month: 'short', year: 'numeric' }).format(date);
};

const startOfIsoWeek = (date) => {
  const d = normalizeDate(date);
  const day = (d.getDay() + 6) % 7; // lundi = 0
  d.setDate(d.getDate() - day);
  return d;
};

// Clé de semaine ISO triable (année-Wsemaine) basée sur le lundi de la semaine.
const isoWeekKey = (date) => {
  const monday = startOfIsoWeek(date);
  const thursday = new Date(monday.getTime() + 3 * msPerDay); // jeudi de la semaine
  const year = thursday.getFullYear();
  const firstThursday = new Date(year, 0, 4);
  const firstMonday = startOfIsoWeek(firstThursday);
  const week = 1 + Math.round((monday - firstMonday) / (7 * msPerDay));
  return `${year}-W${String(week).padStart(2, '0')}`;
};

const isoWeekLabel = (date) => {
  const monday = startOfIsoWeek(date);
  return `${String(monday.getDate()).padStart(2, '0')}/${String(monday.getMonth() + 1).padStart(2, '0')}`;
};

const daysInMonthForKey = (key) => {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) {
    return 30;
  }
  return new Date(year, month, 0).getDate();
};

const monthKeyToDate = (key) => {
  const [year, month] = key.split('-').map(Number);
  return new Date(year, (month || 1) - 1, 1);
};

// Liste continue de clés de mois (YYYY-MM) entre deux dates, bornes incluses.
const enumerateMonthKeys = (startDate, endDate) => {
  const keys = [];
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  let guard = 0;
  while (cursor <= end && guard < 600) {
    keys.push(monthKeyForDate(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
    guard += 1;
  }
  return keys;
};

// Liste continue des semaines ISO (clé + label lundi) entre deux dates.
const enumerateWeeks = (startDate, endDate) => {
  const weeks = [];
  let cursor = startOfIsoWeek(startDate);
  const end = startOfIsoWeek(endDate);
  let guard = 0;
  while (cursor <= end && guard < 600) {
    weeks.push({ key: isoWeekKey(cursor), label: isoWeekLabel(cursor) });
    cursor = new Date(cursor.getTime() + 7 * msPerDay);
    guard += 1;
  }
  return weeks;
};

const shouldIgnoreReservation = (overlay) => {
  return Boolean(overlay && typeof overlay === 'object' && overlay.bookingMode === 'errore');
};

const GestionePrenotazioni = () => {
  const [authRole, setAuthRoleState] = useState(() => getAuthRole());
  const isAdmin = authRole === 'admin';

  const [selectedKey, setSelectedKey] = useState('');
  const [overlays, setOverlays] = useState({});
  const [isLoadingOverlays, setIsLoadingOverlays] = useState(true);
  const [overlaysError, setOverlaysError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  const [form, setForm] = useState({
    name: '',
    adults: 0,
    children: 0,
    beds: { ...defaultBeds },
    manualSlot: false,
    bookingMode: '',
    bookingDate: '',
    country: '',
    totalNet: 0,
    totalGross: 0,
    commission: 0,
    taxPaid: 0,
    pricePerNight: 0,
    extraPerson: 0,
    notes: '',
    comments: [],
  });

  const [newComment, setNewComment] = useState('');

  const [stayFrom, setStayFrom] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [stayTo, setStayTo] = useState(() => `${new Date().getFullYear()}-12-31`);
  const [bookingFrom, setBookingFrom] = useState('');
  const [bookingTo, setBookingTo] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [estimatedTaxRate, setEstimatedTaxRate] = useState(21);
  const [cleaningCost, setCleaningCost] = useState(180);
  const [selectedStayMonth, setSelectedStayMonth] = useState(null);
  const [monthDetailChoice, setMonthDetailChoice] = useState('');

  const [manualStart, setManualStart] = useState('');
  const [manualEnd, setManualEnd] = useState('');
  const [manualError, setManualError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const editorRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    i18n.changeLanguage('it');
  }, []);

  useEffect(() => {
    return subscribeAuthRole(setAuthRoleState);
  }, []);

  const handleLogin = (role) => {
    if (role !== 'admin') {
      return;
    }
    setAuthRole('admin');
  };

  const handleLogout = () => {
    clearAuth();
  };

  const { ranges, isLoading: isLoadingIcal, error: icalError } = useIcalUnavailableRanges({
    withSources: true,
    enabled: isAdmin,
  });

  const reservedRanges = useMemo(() => {
    return (ranges || [])
      .filter((range) => range?.start && range?.end)
      .map((range) => ({
        ...range,
        start: normalizeDate(range.start),
        end: normalizeDate(range.end),
      }))
      .sort((a, b) => a.start - b.start);
  }, [ranges]);

  const combinedRanges = useMemo(() => {
    const map = new Map();
    const added = [];

    const registerRange = (key, range) => {
      map.set(key, range);
      added.push({ start: normalizeDate(range.start), end: normalizeDate(range.end) });
    };

    // 1) Réservations encore renvoyées par le flux iCal (Airbnb / Booking) = vérité.
    //    Ajoutées en premier (jamais évincées) ; on saute un doublon exact ou un
    //    chevauchement (ex. même séjour bloqué à la fois sur Airbnb et Booking).
    for (const range of reservedRanges) {
      const key = reservationKeyForRange(range);
      if (!key || map.has(key)) {
        continue;
      }
      if (added.some((r) => rangesOverlap(r, range))) {
        continue;
      }
      registerRange(key, { ...range, manualSlot: false });
    }

    // 2) Overlays absents du flux : créneaux manuels ou réservations passées/annulées
    //    que Airbnb/Booking ne renvoient plus. On matérialise la surcouche saisie en
    //    ignorant les entrées vides. Comme une villa (une seule unité) ne peut PAS avoir
    //    deux réservations qui se chevauchent, tout chevauchement = doublon/erreur : on
    //    l'écarte. On traite les séjours du plus court au plus rempli en premier, ce qui
    //    élimine automatiquement les blocs aberrants (ex. un faux blocage de 4 mois).
    const overlayEntries = Object.entries(overlays || {})
      .filter(([, overlay]) => overlayHasContent(overlay))
      .map(([key, overlay]) => {
        const [fromYmd, toYmdPart] = key.split('_');
        const start = dateForKeyPart(fromYmd);
        const end = dateForKeyPart(toYmdPart);
        return { key, overlay, start, end };
      })
      .filter((entry) => entry.start && entry.end)
      .sort((a, b) => {
        const durationA = dayDiff(a.end, a.start);
        const durationB = dayDiff(b.end, b.start);
        if (durationA !== durationB) {
          return durationA - durationB; // séjour le plus court d'abord
        }
        return overlayRichness(b.overlay) - overlayRichness(a.overlay); // puis le plus rempli
      });

    for (const { key, overlay, start, end } of overlayEntries) {
      if (map.has(key)) {
        continue;
      }
      if (added.some((r) => rangesOverlap(r, { start, end }))) {
        continue;
      }
      registerRange(key, {
        start,
        end,
        sourceId: null,
        sourceLabel: null,
        manualSlot: Boolean(overlay.manualSlot),
      });
    }

    // 3) Période explicitement sélectionnée mais pas encore connue (création en cours).
    if (selectedKey && !map.has(selectedKey)) {
      const [fromYmd, toYmdPart] = selectedKey.split('_');
      const start = dateForKeyPart(fromYmd);
      const end = dateForKeyPart(toYmdPart);
      if (start && end) {
        registerRange(selectedKey, {
          start,
          end,
          sourceId: null,
          sourceLabel: null,
          manualSlot: true,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.start - b.start);
  }, [overlays, reservedRanges, selectedKey]);

  const visibleReservedRanges = useMemo(() => {
    const today = normalizeDate(new Date());
    const stayFromDate = parseYmdToDate(stayFrom);
    const stayToDate = parseYmdToDate(stayTo);
    return combinedRanges.filter((range) => {
      const key = reservationKeyForRange(range);
      const overlay = overlays[key] || null;
      if (shouldIgnoreReservation(overlay)) {
        return false;
      }
      const name = overlay && typeof overlay.name === 'string' ? overlay.name.trim() : '';
      if (!name && range.start && normalizeDate(range.start).getTime() === today.getTime()) {
        return false;
      }
      // Les slots manuels sont toujours visibles (pour pouvoir les supprimer malgré le filtre).
      const isManual = Boolean(range.manualSlot || (overlay && overlay.manualSlot));
      if (!isManual) {
        if (stayFromDate && range.start < stayFromDate) {
          return false;
        }
        if (stayToDate && range.start > stayToDate) {
          return false;
        }
      }

      if (siteFilter !== 'all') {
        const bookingMode =
          (overlay && typeof overlay.bookingMode === 'string' ? overlay.bookingMode : '') ||
          getDefaultBookingMode(range);
        if (bookingMode !== siteFilter) {
          return false;
        }
      }

      return true;
    });
  }, [combinedRanges, overlays, siteFilter, stayFrom, stayTo]);

  // Positionne la liste (scrollable) sur la réservation en cours / prochaine.
  // On lit le DOM réel (1re ligne non « passée ») : offsetTop de la ligne cible −
  // offsetTop de la 1re ligne = position dans le contenu scrollable. Plusieurs passes
  // (paint, délais, chargement des polices) car les polices custom recalculent la mise
  // en page après coup et décaleraient un scroll trop précoce.
  useEffect(() => {
    if (isLoadingOverlays || isLoadingIcal) {
      return undefined;
    }
    let cancelled = false;
    const applyScroll = () => {
      if (cancelled) {
        return;
      }
      const container = listRef.current;
      if (!container) {
        return;
      }
      const items = Array.from(container.querySelectorAll('li'));
      const firstLi = items[0];
      const target =
        items.find((li) => !li.querySelector('.prenotazioni-item--past')) || firstLi;
      if (!firstLi || !target) {
        return;
      }
      container.scrollTop = Math.max(0, target.offsetTop - firstLi.offsetTop);
    };

    const raf = window.requestAnimationFrame(applyScroll);
    const t1 = window.setTimeout(applyScroll, 250);
    const t2 = window.setTimeout(applyScroll, 700);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(applyScroll).catch(() => {});
    }

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isLoadingOverlays, isLoadingIcal, visibleReservedRanges.length]);

  const selectedRange = useMemo(() => {
    const fromKnown = combinedRanges.find((range) => reservationKeyForRange(range) === selectedKey) || null;
    if (fromKnown) {
      return fromKnown;
    }

    const parsed = parseReservationKey(selectedKey);
    if (!parsed) {
      return null;
    }
    return {
      start: parsed.start,
      end: parsed.end,
      sourceId: null,
      sourceLabel: null,
      manualSlot: true,
    };
  }, [combinedRanges, selectedKey]);

  const isManualSelected = useMemo(() => {
    const fromOverlay = overlays[selectedKey];
    const overlayManual = Boolean(fromOverlay && typeof fromOverlay === 'object' && fromOverlay.manualSlot);
    return Boolean(overlayManual || form.manualSlot || selectedRange?.manualSlot);
  }, [form.manualSlot, overlays, selectedKey, selectedRange]);

  useEffect(() => {
    if (!selectedKey) {
      return;
    }

    const overlay = overlays[selectedKey];
    if (shouldIgnoreReservation(overlay)) {
      setSelectedKey('');
    }
  }, [overlays, selectedKey]);

  const statsItems = useMemo(() => {
    const bookingFromDate = parseYmdToDate(bookingFrom);
    const bookingToDate = parseYmdToDate(bookingTo);

    return visibleReservedRanges
      .map((range) => {
        const key = reservationKeyForRange(range);
        const overlay = overlays[key] || null;

        const bookingMode =
          (overlay && typeof overlay.bookingMode === 'string' ? overlay.bookingMode : '') ||
          getDefaultBookingMode(range);

        if (bookingMode === 'sejour_proprietaire') {
          return null;
        }

        const bookingDate = overlay && typeof overlay.bookingDate === 'string' ? overlay.bookingDate : '';
        const bookingDateObj = parseYmdToDate(bookingDate);

        const totalNet = overlay && Number.isFinite(overlay.totalNet) ? overlay.totalNet : 0;
        const totalGross = overlay && Number.isFinite(overlay.totalGross) ? overlay.totalGross : 0;
        const taxPaid = overlay && Number.isFinite(overlay.taxPaid) ? overlay.taxPaid : 0;
        const commission = deriveCommissionAmount({
          totalNet,
          totalGross,
          commission: overlay && Number.isFinite(overlay.commission) ? overlay.commission : 0,
          taxPaid,
        });
        const realIncome = deriveRealIncome({
          totalNet,
          totalGross,
          commission,
          taxPaid,
        });
        const commissionRate = totalNet > 0 ? Math.min(1, Math.max(0, commission / totalNet)) : null;

        return {
          key,
          start: range.start,
          end: range.end,
          nights: diffDays(range.start, range.end),
          bookingMode: bookingMode || '—',
          country: (overlay && typeof overlay.country === 'string' ? overlay.country : '') || '—',
          bookingDate: bookingDateObj,
          totalNet,
          totalGross,
          taxPaid,
          commission,
          realIncome,
          commissionRate,
          pricePerNight: overlay && Number.isFinite(overlay.pricePerNight) ? overlay.pricePerNight : 0,
          extraPerson: overlay && Number.isFinite(overlay.extraPerson) ? overlay.extraPerson : 0,
        };
      })
      .filter(Boolean)
      .filter((item) => {
        const bookingFilterActive = Boolean(bookingFromDate || bookingToDate);
        if (bookingFilterActive && !item.bookingDate) {
          return false;
        }
        if (bookingFromDate && item.bookingDate && item.bookingDate < bookingFromDate) {
          return false;
        }
        if (bookingToDate && item.bookingDate && item.bookingDate > bookingToDate) {
          return false;
        }

        return true;
      });
  }, [bookingFrom, bookingTo, overlays, visibleReservedRanges]);

  const handleCreateManualSlot = () => {
    setManualError('');

    const startDate = parseYmdToDate(manualStart);
    const endDate = parseYmdToDate(manualEnd);

    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
      setManualError('Inserisci due date valide.');
      return;
    }

    if (endDate <= startDate) {
      setManualError('La data di partenza deve essere dopo la data di arrivo.');
      return;
    }

    const key = `${toYmd(startDate)}_${toYmd(endDate)}`;
    requestSelectKey(key);

    if (!overlays[key]) {
      setForm({
        ...defaultFormForRange({ start: startDate, end: endDate, manualSlot: true }),
        manualSlot: true,
      });
      setSaveStatus('');
      setNewComment('');
    }

    setManualStart('');
    setManualEnd('');
  };

  const statsKpis = useMemo(() => {
    const reservations = statsItems.length;
    const nights = statsItems.reduce((sum, item) => sum + (item.nights || 0), 0);
    // totalNet stocké = lordo (prix locataire), totalGross stocké = netto saisi
    const totalLordo = statsItems.reduce((sum, item) => sum + (item.totalNet || 0), 0);
    const totalNetto = statsItems.reduce((sum, item) => sum + (item.totalGross || 0), 0);
    const totalCommission = statsItems.reduce((sum, item) => sum + (item.commission || 0), 0);
    const totalTaxPaid = statsItems.reduce((sum, item) => sum + (item.taxPaid || 0), 0);
    const totalRealIncome = statsItems.reduce((sum, item) => sum + (item.realIncome || 0), 0);
    const safeCleaningCost = Math.max(0, Number(cleaningCost) || 0);
    const totalCleaningExpense = statsItems.reduce((sum) => sum + safeCleaningCost, 0);
    const totalRealIncomeAfterCleaning = totalRealIncome - totalCleaningExpense;

    const safeEstimatedTaxRate = Math.max(0, Number(estimatedTaxRate) || 0) / 100;
    const totalEstimatedTax = statsItems.reduce((sum, item) => sum + ((item.totalNet || 0) * safeEstimatedTaxRate), 0);
    const totalTaxRemaining = Math.max(0, totalEstimatedTax - totalTaxPaid);
    const averageCommissionRate = totalLordo > 0 ? totalCommission / totalLordo : null;

    let avgPricePerNight = 0;
    if (nights > 0 && totalRealIncome > 0) {
      avgPricePerNight = totalRealIncome / nights;
    } else {
      const values = statsItems.map((item) => item.pricePerNight).filter((value) => value > 0);
      if (values.length > 0) {
        avgPricePerNight = values.reduce((a, b) => a + b, 0) / values.length;
      }
    }

    return {
      reservations,
      nights,
      totalLordo,
      totalNetto,
      totalCommission,
      totalTaxPaid,
      totalEstimatedTax,
      totalTaxRemaining,
      totalRealIncome,
      totalCleaningExpense,
      totalRealIncomeAfterCleaning,
      averageCommissionRate,
      avgPricePerNight,
    };
  }, [cleaningCost, estimatedTaxRate, statsItems]);

  // Bornes de l'axe temporel : période du filtre (par défaut l'année en cours),
  // étendue pour englober toute donnée visible en dehors (créneaux manuels…).
  // Sert à afficher l'année complète, mois à zéro compris, sur tous les graphes.
  const yearAxis = useMemo(() => {
    let start = parseYmdToDate(stayFrom);
    let end = parseYmdToDate(stayTo);
    const year = new Date().getFullYear();
    if (!start) {
      start = new Date(year, 0, 1);
    }
    if (!end) {
      end = new Date(year, 11, 31);
    }
    start = normalizeDate(start);
    end = normalizeDate(end);
    for (const item of statsItems) {
      if (item.start instanceof Date && normalizeDate(item.start) < start) {
        start = normalizeDate(item.start);
      }
      if (item.end instanceof Date) {
        const lastNight = normalizeDate(new Date(normalizeDate(item.end).getTime() - msPerDay));
        if (lastNight > end) {
          end = lastNight;
        }
      }
    }
    if (end < start) {
      end = start;
    }
    return { start, end };
  }, [stayFrom, stayTo, statsItems]);

  const axisMonthKeys = useMemo(() => enumerateMonthKeys(yearAxis.start, yearAxis.end), [yearAxis]);
  const axisWeeks = useMemo(() => enumerateWeeks(yearAxis.start, yearAxis.end), [yearAxis]);

  const monthlyData = useMemo(() => {
    const buckets = new Map();
    for (const item of statsItems) {
      const key = monthKeyForDate(item.start);
      const existing = buckets.get(key) || {
        monthKey: key,
        month: monthLabelForKey(key),
        reservations: 0,
        nights: 0,
        totalNet: 0,
        totalGross: 0,
        commission: 0,
        realIncome: 0,
      };

      existing.reservations += 1;
      existing.nights += item.nights || 0;
      existing.totalNet += item.totalNet || 0;
      existing.totalGross += item.totalGross || 0;
      existing.commission += item.commission || 0;
      existing.realIncome += item.realIncome || 0;
      buckets.set(key, existing);
    }

    return Array.from(buckets.values()).sort((a, b) => (a.monthKey < b.monthKey ? -1 : 1));
  }, [statsItems]);

  // CA net (netto) + bénéfice net final (après commission, impôt et ménage) par mois.
  // Zéro-remplissage sur toute l'année (mois sans réservation inclus).
  const monthlyChartData = useMemo(() => {
    const safeCleaning = Math.max(0, Number(cleaningCost) || 0);
    const byKey = new Map(monthlyData.map((month) => [month.monthKey, month]));
    return axisMonthKeys.map((key) => {
      const month = byKey.get(key);
      const reservations = month?.reservations || 0;
      const realIncome = month?.realIncome || 0;
      return {
        monthKey: key,
        month: monthLabelForKey(key),
        reservations,
        nights: month?.nights || 0,
        totalNet: month?.totalNet || 0,
        totalGross: month?.totalGross || 0,
        commission: month?.commission || 0,
        realIncome,
        netProfit: realIncome - safeCleaning * reservations,
      };
    });
  }, [monthlyData, axisMonthKeys, cleaningCost]);

  // Même chose par semaine ISO, zéro-remplie sur toute la période (semaines vides incluses).
  const weeklyChartData = useMemo(() => {
    const safeCleaning = Math.max(0, Number(cleaningCost) || 0);
    const buckets = new Map();
    for (const item of statsItems) {
      if (!(item.start instanceof Date)) {
        continue;
      }
      const key = isoWeekKey(item.start);
      const existing = buckets.get(key) || { reservations: 0, totalGross: 0, realIncome: 0 };
      existing.reservations += 1;
      existing.totalGross += item.totalGross || 0;
      existing.realIncome += item.realIncome || 0;
      buckets.set(key, existing);
    }
    return axisWeeks.map(({ key, label }) => {
      const week = buckets.get(key) || { reservations: 0, totalGross: 0, realIncome: 0 };
      return {
        weekKey: key,
        week: label,
        reservations: week.reservations,
        totalGross: week.totalGross,
        realIncome: week.realIncome,
        netProfit: week.realIncome - safeCleaning * week.reservations,
      };
    });
  }, [statsItems, axisWeeks, cleaningCost]);

  // Taux de remplissage (nuits réservées / nuits du mois), nuit par nuit pour bien
  // répartir les séjours à cheval sur deux mois.
  const occupancyByMonth = useMemo(() => {
    const nights = new Map();
    for (const item of statsItems) {
      if (!(item.start instanceof Date) || !(item.end instanceof Date)) {
        continue;
      }
      let cursor = normalizeDate(item.start);
      const end = normalizeDate(item.end);
      while (cursor < end) {
        const key = monthKeyForDate(cursor);
        nights.set(key, (nights.get(key) || 0) + 1);
        cursor = new Date(cursor.getTime() + msPerDay);
      }
    }
    return axisMonthKeys.map((key) => {
      const bookedNights = nights.get(key) || 0;
      const days = daysInMonthForKey(key);
      return {
        monthKey: key,
        month: monthLabelForKey(key),
        bookedNights,
        occupancy: days > 0 ? Math.min(1, bookedNights / days) : 0,
      };
    });
  }, [statsItems, axisMonthKeys]);

  // Récapitulatif du mois sélectionné dans le formulaire de détail.
  const selectedStayMonthSummary = useMemo(() => {
    if (!selectedStayMonth) {
      return null;
    }
    return monthlyData.find((entry) => entry.monthKey === selectedStayMonth) || null;
  }, [monthlyData, selectedStayMonth]);

  // Pour le mois de séjour sélectionné (formulaire de détail), répartit les
  // réservations de ce mois selon leur date de réservation (data prenotazione).
  const stayMonthBookingBreakdown = useMemo(() => {
    if (!selectedStayMonth) {
      return [];
    }
    const buckets = new Map();
    let unknown = null;
    for (const item of statsItems) {
      if (monthKeyForDate(item.start) !== selectedStayMonth) {
        continue;
      }
      if (!(item.bookingDate instanceof Date)) {
        unknown = unknown || { reservations: 0, totalNet: 0, totalGross: 0 };
        unknown.reservations += 1;
        unknown.totalNet += item.totalNet || 0;
        unknown.totalGross += item.totalGross || 0;
        continue;
      }
      const key = monthKeyForDate(item.bookingDate);
      const existing = buckets.get(key) || { reservations: 0, totalNet: 0, totalGross: 0 };
      existing.reservations += 1;
      existing.totalNet += item.totalNet || 0;
      existing.totalGross += item.totalGross || 0;
      buckets.set(key, existing);
    }

    // Axe complet : du plus tôt (début d'année ou 1re réservation) jusqu'au mois du
    // séjour, mois vides à zéro inclus.
    const presentKeys = Array.from(buckets.keys());
    const startKey = [monthKeyForDate(yearAxis.start), ...presentKeys].reduce(
      (min, key) => (key < min ? key : min)
    );
    const endKey = [selectedStayMonth, monthKeyForDate(yearAxis.end), ...presentKeys].reduce(
      (max, key) => (key > max ? key : max)
    );
    const rows = enumerateMonthKeys(monthKeyToDate(startKey), monthKeyToDate(endKey)).map((key) => {
      const bucket = buckets.get(key) || { reservations: 0, totalNet: 0, totalGross: 0 };
      return { monthKey: key, month: monthLabelForKey(key), ...bucket };
    });

    if (unknown) {
      rows.push({ monthKey: '—', month: 'Sconosciuta', ...unknown });
    }
    return rows;
  }, [selectedStayMonth, statsItems, yearAxis]);

  // Le mois sélectionné peut disparaître si les filtres changent : on nettoie.
  useEffect(() => {
    if (selectedStayMonth && !monthlyData.some((entry) => entry.monthKey === selectedStayMonth)) {
      setSelectedStayMonth(null);
    }
  }, [monthlyData, selectedStayMonth]);

  const bookingModeData = useMemo(() => {
    const buckets = new Map();
    for (const item of statsItems) {
      const key = item.bookingMode || '—';
      buckets.set(key, (buckets.get(key) || 0) + 1);
    }
    return Array.from(buckets.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [statsItems]);

  const bookingModeNetData = useMemo(() => {
    const buckets = new Map();
    for (const item of statsItems) {
      const key = item.bookingMode || '—';
      const existing = buckets.get(key) || { name: key, totalNet: 0, totalGross: 0, commission: 0, reservations: 0 };
      existing.totalNet += item.totalNet || 0;
      existing.totalGross += item.totalGross || 0;
      existing.commission += item.commission || 0;
      existing.reservations += 1;
      buckets.set(key, existing);
    }

    return Array.from(buckets.values()).sort((a, b) => b.totalNet - a.totalNet);
  }, [statsItems]);

  // Camembert du CA net (netto) par plateforme.
  const bookingModeNetPie = useMemo(
    () =>
      bookingModeNetData
        .map((entry) => ({ name: entry.name, value: entry.totalGross }))
        .filter((entry) => entry.value > 0),
    [bookingModeNetData]
  );

  const countryData = useMemo(() => {
    const buckets = new Map();
    for (const item of statsItems) {
      const key = item.country || '—';
      buckets.set(key, (buckets.get(key) || 0) + 1);
    }
    return Array.from(buckets.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [statsItems]);

  const bookingModeColor = (mode) => {
    if (mode === 'Airbnb' || mode === 'airbnb') {
      return 'var(--brand-airbnb)';
    }
    if (mode === 'Booking' || mode === 'booking') {
      return 'var(--brand-booking)';
    }
    if (mode === 'Vrbo' || mode === 'vrbo') {
      return 'var(--brand-shortstay)';
    }
    return 'var(--accent-hover)';
  };

  const baselineForm = useMemo(() => {
    if (!selectedKey) {
      return null;
    }

    const existing = overlays[selectedKey];
    if (existing) {
      return overlayToForm(existing);
    }

    return defaultFormForRange(selectedRange);
  }, [overlays, selectedKey, selectedRange]);

  const isDirty = useMemo(() => {
    if (!selectedKey || !baselineForm) {
      return false;
    }

    return !isSameForm(form, baselineForm);
  }, [baselineForm, form, selectedKey]);

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    let isCancelled = false;

    const load = async () => {
      try {
        setIsLoadingOverlays(true);
        setOverlaysError('');

        const response = await fetch('/api/bookings/overlays.php', { cache: 'no-store' });
        const payload = await response.json();
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || 'Impossibile caricare i dati.');
        }

        const loaded = payload?.data?.overlays || {};
        if (!isCancelled) {
          setOverlays(loaded);
        }
      } catch (err) {
        if (!isCancelled) {
          setOverlaysError(err?.message || 'Errore di caricamento.');
          setOverlays({});
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingOverlays(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!selectedKey) {
      return;
    }

    const existing = overlays[selectedKey];
    if (existing) {
      setForm(overlayToForm(existing));
      setNewComment('');
      setSaveStatus('');
      return;
    }

    setForm(defaultFormForRange(selectedRange));
    setNewComment('');
    setSaveStatus('');
  }, [selectedKey, overlays, selectedRange]);

  const requestSelectKey = (nextKey) => {
    if (nextKey === selectedKey) {
      return true;
    }

    if (isDirty) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm('Hai modifiche non salvate. Vuoi cambiare periodo senza salvare?');
      if (!confirmed) {
        return false;
      }
    }

    setSelectedKey(nextKey);
    return true;
  };

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setSaveStatus('');
  };

  const handleCalculateNetFromLordoAndCommission = () => {
    const lordo = Number.isFinite(form.totalNet) ? form.totalNet : 0;
    const comm = Number.isFinite(form.commission) ? form.commission : 0;
    if (lordo <= 0 || comm < 0) {
      return;
    }

    updateForm({ totalGross: roundTo2(Math.max(0, lordo - comm)) });
  };

  const scrollToEditor = () => {
    if (editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateBeds = (key, value) => {
    setForm((prev) => ({
      ...prev,
      beds: { ...prev.beds, [key]: value }
    }));
    setSaveStatus('');
  };

  const handleSendRecap = () => {
    if (!selectedKey || !selectedRange) {
      return;
    }

    const nights = diffDays(selectedRange.start, selectedRange.end);
    const bedsActive = Object.entries(form.beds || {})
      .filter(([, v]) => Boolean(v))
      .map(([k]) => k);
    const adults = Number.isFinite(form.adults) ? form.adults : 0;
    const children = Number.isFinite(form.children) ? form.children : 0;
    const bedsLabel = bedsActive.map((k) => BED_LABELS[k] || k).join(', ') || '—';

    const msgLines = [
      `📋 *Riepilogo prenotazione*`,
      ``,
      `📅 ${formatDate(selectedRange.start)} → ${formatDate(selectedRange.end)}`,
      `🌙 ${nights} notti`,
      `👥 ${adults} adulti, ${children} bambini`,
    ];
    msgLines.push(`🛏️ Da preparare : ${bedsLabel}`);
    if (form.notes) msgLines.push(`📝 Osservazioni : ${form.notes}`);

    const comments = Array.isArray(form.comments) ? form.comments : [];
    if (comments.length > 0) {
      msgLines.push(``, `💬 Commenti :`);
      for (const c of comments) {
        const dateStr = c.at ? new Date(c.at).toLocaleString('it-IT') : '';
        msgLines.push(`  • ${dateStr ? `[${dateStr}] ` : ''}${c.text || ''}`);
      }
    }

    fetch('https://n8n.atoneo.com/webhook/commentaire-site', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ type: 'reservation_recap', message: msgLines.join('\n') }),
    }).catch(() => {
      // Ne bloque pas l'UI si le webhook est indisponible.
    });
  };

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!text || !selectedKey) {
      return;
    }

    const at = new Date().toISOString();
    const updatedComments = [...(form.comments || []), { at, text }];
    const updatedForm = { ...form, comments: updatedComments };

    const startLabel = selectedRange?.start ? formatDate(selectedRange.start) : '';
    const endLabel = selectedRange?.end ? formatDate(selectedRange.end) : '';
    const bedsLabel = Object.entries(form.beds || {})
      .filter(([, v]) => v === true)
      .map(([k]) => BED_LABELS[k] || k)
      .join(', ') || '—';

    const msgLines = [
      `🏡 *Nuova informazione sulla prenotazione*`,
      ``,
      `💬 *Nuovo commento :* ${text}`,
      ``,
      `📅 ${startLabel} → ${endLabel}`,
      `👥 ${Number.isFinite(form.adults) ? form.adults : 0} adulti, ${Number.isFinite(form.children) ? form.children : 0} bambini`,
      `🛏️ Da preparare : ${bedsLabel}`,
    ];
    if (form.notes) msgLines.push(`📝 Osservazioni : ${form.notes}`);

    fetch('https://n8n.atoneo.com/webhook/commentaire-site', {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ type: 'reservation_comment', message: msgLines.join('\n') }),
    }).catch(() => {
      // Ne bloque pas l'UI si le webhook est indisponible.
    });

    setNewComment('');
    setForm(updatedForm);
    setSaveStatus('Salvataggio…');

    try {
      const response = await fetch('/api/bookings/overlays.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'upsert', reservationKey: selectedKey, overlay: updatedForm }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Errore durante il salvataggio.');
      }
      setOverlays((prev) => ({ ...prev, [selectedKey]: payload.data }));
      setSaveStatus('Salvato');
      window.setTimeout(() => setSaveStatus(''), 1200);
    } catch (err) {
      setSaveStatus('Errore');
    }
  };

  const saveOverlay = async () => {
    if (!selectedKey) {
      return;
    }

    const isNewCreation = !overlays[selectedKey];
    setSaveStatus('Salvataggio…');

    try {
      const response = await fetch('/api/bookings/overlays.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'upsert',
          reservationKey: selectedKey,
          overlay: form,
        })
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Errore durante il salvataggio.');
      }

      setOverlays((prev) => ({ ...prev, [selectedKey]: payload.data }));
      setSaveStatus('Salvato');

      if (isNewCreation && selectedRange) {
        const nights = diffDays(selectedRange.start, selectedRange.end);
        const bedsActive = Object.entries(form.beds || {})
          .filter(([, v]) => Boolean(v))
          .map(([k]) => BED_LABELS[k] || k)
          .join(', ') || '—';
        const msgLines = [
          `🆕 *Nuova prenotazione registrata*`,
          ``,
          `📅 ${formatDate(selectedRange.start)} → ${formatDate(selectedRange.end)}`,
          `🌙 ${nights} notti`,
          `👥 ${Number.isFinite(form.adults) ? form.adults : 0} adulti, ${Number.isFinite(form.children) ? form.children : 0} bambini`,
          `🛏️ Da preparare : ${bedsActive}`,
        ];
        if (form.notes) msgLines.push(`📝 Osservazioni : ${form.notes}`);
        fetch('https://n8n.atoneo.com/webhook/commentaire-site', {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify({ type: 'reservation_new', message: msgLines.join('\n') }),
        }).catch(() => {});
      }

      window.setTimeout(() => {
        setSaveStatus('');
      }, 1200);
    } catch (err) {
      setSaveStatus('Errore');
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Errore durante il salvataggio.');
    }
  };

  const deleteOverlay = async () => {
    if (!selectedKey) {
      return;
    }

    const isManual = Boolean(
      (overlays[selectedKey] && typeof overlays[selectedKey] === 'object' && overlays[selectedKey].manualSlot) ||
        form.manualSlot ||
        selectedRange?.manualSlot
    );

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      isManual
        ? 'Eliminare questo periodo manuale?'
        : 'Eliminare i dati di questa prenotazione?'
    );
    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch('/api/bookings/overlays.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          reservationKey: selectedKey,
        })
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Errore durante la cancellazione.');
      }

      setOverlays((prev) => {
        const next = { ...prev };
        delete next[selectedKey];
        return next;
      });

      if (selectedRange) {
        const msgLines = [
          `🗑️ *Prenotazione eliminata*`,
          ``,
          `📅 ${formatDate(selectedRange.start)} → ${formatDate(selectedRange.end)}`,
        ];
        if (form.name) msgLines.push(`👤 ${form.name}`);
        fetch('https://n8n.atoneo.com/webhook/commentaire-site', {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body: JSON.stringify({ type: 'reservation_deleted', message: msgLines.join('\n') }),
        }).catch(() => {});
      }

      if (isManual) {
        setSelectedKey('');
        setNewComment('');
        setSaveStatus('');
        return;
      }

      setForm({
        name: '',
        adults: 0,
        children: 0,
        beds: { ...defaultBeds },
        manualSlot: false,
        bookingMode: '',
        bookingDate: '',
        country: '',
        totalNet: 0,
        totalGross: 0,
        commission: 0,
        taxPaid: 0,
        pricePerNight: 0,
        extraPerson: 0,
        notes: '',
        comments: [],
      });
      setSaveStatus('');
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Errore durante la cancellazione.');
    }
  };

  if (!isAdmin) {
    return (
      <LoginForm
        onLogin={handleLogin}
        labels={{
          title: 'Gestione Prenotazioni',
          placeholder: 'Inserisci la password',
          submit: 'Accedi',
          errorInvalid: 'Password non corretta'
        }}
      />
    );
  }

  return (
    <div className="site-shell admin-shell">
      <AdminSidebar
        onLogout={handleLogout}
        sitePath="/it/affitto-villa-appartamento-sardegna-san-teodoro"
        role={authRole}
      />

      <main className="site-shell-content admin-content">
        <div className="admin-panel prenotazioni-panel">
          <h2>Gestione Prenotazioni</h2>
          <p>Compila le informazioni per ogni periodo prenotato (overlay salvato in JSON).</p>

          {/* On attend le chargement du calendrier ET des overlays (noms, importi…)
              pour ne pas afficher des réservations vides le temps que tout arrive. */}
          {(isLoadingIcal || isLoadingOverlays) && (
            <div className="prenotazioni-loader" role="status" aria-live="polite">
              <span className="prenotazioni-spinner" aria-hidden="true" />
              <span>Caricamento delle prenotazioni…</span>
            </div>
          )}
          {!isLoadingIcal && icalError && <p className="admin-error">{icalError}</p>}
          {!isLoadingOverlays && overlaysError && <p className="admin-error">{overlaysError}</p>}

          {!isLoadingIcal && !isLoadingOverlays && !icalError && visibleReservedRanges.length === 0 && (
            <p>Nessuna prenotazione trovata.</p>
          )}

          {!isLoadingIcal && !isLoadingOverlays && !icalError && visibleReservedRanges.length > 0 && (
            <>
              {/* ── Barre d'en-tête ────────────────────────── */}
              <div className="prenotazioni-header-bar">
                <div className="prenotazioni-header-filters">
                  <label className="prenotazioni-header-label">
                    Periodo
                    <div className="prenotazioni-header-dates">
                      <input
                        type="date"
                        value={stayFrom}
                        onChange={(e) => setStayFrom(e.target.value)}
                      />
                      <span className="prenotazioni-header-sep">→</span>
                      <input
                        type="date"
                        value={stayTo}
                        onChange={(e) => setStayTo(e.target.value)}
                      />
                      <button
                        type="button"
                        className="admin-secondary-btn prenotazioni-header-reset"
                        onClick={() => {
                          const y = new Date().getFullYear();
                          setStayFrom(`${y}-01-01`);
                          setStayTo(`${y}-12-31`);
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </label>
                  <label className="prenotazioni-header-label">
                    Sito
                    <select
                      value={siteFilter}
                      onChange={(e) => setSiteFilter(e.target.value)}
                    >
                      <option value="all">Tutti</option>
                      <option value="airbnb">Airbnb</option>
                      <option value="booking">Booking</option>
                    </select>
                  </label>
                </div>
                <div className="prenotazioni-header-kpi">
                  <span className="prenotazioni-header-kpi-label">Lordo cumulato</span>
                  <span className="prenotazioni-header-kpi-value">
                    {currencyFormatter.format(statsKpis.totalLordo)}
                  </span>
                </div>
                <div className="prenotazioni-header-kpi">
                  <span className="prenotazioni-header-kpi-label">Netto cumulato</span>
                  <span className="prenotazioni-header-kpi-value prenotazioni-header-kpi-value--green">
                    {currencyFormatter.format(statsKpis.totalNetto)}
                  </span>
                </div>
                <div className="prenotazioni-header-kpi">
                  <span className="prenotazioni-header-kpi-label">% commissione</span>
                  <span className="prenotazioni-header-kpi-value">
                    {typeof statsKpis.averageCommissionRate === 'number'
                      ? percentFormatter.format(statsKpis.averageCommissionRate)
                      : '—'}
                  </span>
                </div>
                <div className="prenotazioni-header-kpi">
                  <span className="prenotazioni-header-kpi-label">Prenotazioni</span>
                  <span className="prenotazioni-header-kpi-value">{statsKpis.reservations}</span>
                </div>
              </div>

              {/* ── Modal Créer séjour ──────────────────────── */}
              {showCreateModal && (
                <div
                  className="prenotazioni-modal-overlay"
                  onClick={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}
                >
                  <div className="prenotazioni-modal">
                    <div className="prenotazioni-modal-header">
                      <span className="prenotazioni-modal-title">Créer un séjour</span>
                      <button
                        type="button"
                        className="prenotazioni-modal-close"
                        onClick={() => setShowCreateModal(false)}
                        aria-label="Fermer"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="prenotazioni-modal-body">
                      <label className="prenotazioni-modal-label">
                        Arrivo
                        <input
                          type="date"
                          value={manualStart}
                          onChange={(e) => setManualStart(e.target.value)}
                        />
                      </label>
                      <label className="prenotazioni-modal-label">
                        Partenza
                        <input
                          type="date"
                          value={manualEnd}
                          onChange={(e) => setManualEnd(e.target.value)}
                        />
                      </label>
                      {manualError ? <p className="admin-error">{manualError}</p> : null}
                    </div>
                    <div className="prenotazioni-modal-footer">
                      <button
                        type="button"
                        className="admin-copy-btn"
                        onClick={() => {
                          handleCreateManualSlot();
                          if (!manualError) {
                            setShowCreateModal(false);
                          }
                        }}
                      >
                        Nuovo periodo
                      </button>
                      <button
                        type="button"
                        className="admin-secondary-btn"
                        onClick={() => setShowCreateModal(false)}
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="prenotazioni-layout">
                <aside className="prenotazioni-list">
                <div className="prenotazioni-list-header">
                  <h3>Periodi</h3>
                  <button
                    type="button"
                    className="admin-copy-btn prenotazioni-create-btn"
                    onClick={() => {
                      setManualError('');
                      setShowCreateModal(true);
                    }}
                  >
                    + Créer séjour
                  </button>
                </div>
                <ul ref={listRef}>
                  {visibleReservedRanges.map((range) => {
                    const key = reservationKeyForRange(range);
                    const isActive = key === selectedKey;
                    const isPast = normalizeDate(range.end) <= normalizeDate(new Date());
                    const hasOverlay = Boolean(overlays[key]);
                    const label = `${formatDate(range.start)} → ${formatDate(range.end)}`;
                    const overlay = overlays[key] || null;
                    const name = overlay && typeof overlay.name === 'string' ? overlay.name.trim() : '';
                    const adults = overlay && Number.isFinite(overlay.adults) ? overlay.adults : 0;
                    const children = overlay && Number.isFinite(overlay.children) ? overlay.children : 0;
                    const peopleTotal = adults + children;
                    const totalNet = overlay && Number.isFinite(overlay.totalNet) ? overlay.totalNet : 0;
                    const totalGross = overlay && Number.isFinite(overlay.totalGross) ? overlay.totalGross : 0;
                    const taxPaid = overlay && Number.isFinite(overlay.taxPaid) ? overlay.taxPaid : 0;
                    const { commissionRate, taxRate } = computeRates({ totalGross, totalNet, taxPaid });
                    const bookingModeValue =
                      (overlay && typeof overlay.bookingMode === 'string' ? overlay.bookingMode : '') ||
                      getDefaultBookingMode(range);
                    const bookingModeLabel = bookingModeLabelForValue(bookingModeValue);
                    const bookingModeClass = bookingModeValue ? `prenotazioni-pill--${bookingModeValue}` : '';
                    const bookingItemClass = bookingModeValue ? `prenotazioni-item--${bookingModeValue}` : '';

                    return (
                      <li key={key}>
                        <button
                          type="button"
                          className={`prenotazioni-item ${bookingItemClass} ${isActive ? 'active' : ''} ${isPast ? 'prenotazioni-item--past' : ''}`}
                          onClick={() => {
                            const selected = requestSelectKey(key);
                            if (selected) {
                              scrollToEditor();
                            }
                          }}
                        >
                          <div className="prenotazioni-item-row">
                            <span className="prenotazioni-item-dates">{label}</span>
                            {totalGross > 0
                              ? <span className="prenotazioni-item-amount">{currencyFormatter.format(totalGross)}</span>
                              : null}
                          </div>
                          <div className="prenotazioni-item-sub">
                            {name ? <span className="prenotazioni-item-name">{name}</span> : null}
                            <span className={`prenotazioni-badge prenotazioni-badge--${bookingModeValue || 'none'}`}>
                              {bookingModeLabel}
                            </span>
                          </div>
                          {isActive && (
                            <div className="prenotazioni-item-expanded">
                              {peopleTotal > 0
                                ? <span className="prenotazioni-pill prenotazioni-pill--people">{peopleTotal} pers.</span>
                                : null}
                              {typeof commissionRate === 'number'
                                ? <span className="prenotazioni-pill prenotazioni-pill--commission">{`Comm: ${percentFormatter.format(commissionRate)}`}</span>
                                : null}
                              {typeof taxRate === 'number'
                                ? <span className="prenotazioni-pill prenotazioni-pill--tax">{`Imp: ${percentFormatter.format(taxRate)}`}</span>
                                : null}
                              {!hasOverlay
                                ? <span className="prenotazioni-pill prenotazioni-pill--empty">à renseigner</span>
                                : null}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                </aside>

                <section className="prenotazioni-editor" ref={editorRef}>
                {!selectedRange
                  ? <p className="prenotazioni-hint">Seleziona un periodo a sinistra.</p>
                  : null}

                {selectedRange && (
                  <>
                    <div className="prenotazioni-editor-header">
                      <span className="prenotazioni-editor-period">
                        {formatDate(selectedRange.start)}
                        <span className="prenotazioni-editor-arrow"> → </span>
                        {formatDate(selectedRange.end)}
                        <span className="prenotazioni-editor-nights">
                          {diffDays(selectedRange.start, selectedRange.end)} notti
                        </span>
                      </span>
                      {isManualSelected && <span className="prenotazioni-badge prenotazioni-badge--manual">Manuale</span>}
                    </div>

                    {/* ── Info ospite ───────────────────────────── */}
                    <details className="prenotazioni-section" open>
                      <summary className="prenotazioni-section-summary">Info ospite</summary>
                      <div className="prenotazioni-section-body">
                        <div className="prenotazioni-grid">
                          <label>
                            Nome
                            <input
                              type="text"
                              value={form.name}
                              onChange={(e) => updateForm({ name: e.target.value })}
                            />
                          </label>

                          <label>
                            Modalità prenotazione
                            <select
                              value={form.bookingMode}
                              onChange={(e) => updateForm({ bookingMode: e.target.value })}
                            >
                              {BOOKING_MODES.map((mode) => (
                                <option key={mode.value} value={mode.value}>{mode.label}</option>
                              ))}
                            </select>
                          </label>

                          <label>
                            Adulti
                            <input
                              type="number"
                              min="0"
                              value={form.adults}
                              onChange={(e) => updateForm({ adults: Number(e.target.value) })}
                            />
                          </label>

                          <label>
                            Bambini
                            <input
                              type="number"
                              min="0"
                              value={form.children}
                              onChange={(e) => updateForm({ children: Number(e.target.value) })}
                            />
                          </label>

                          <label>
                            Paese
                            <CountryAutocomplete
                              value={form.country}
                              onChange={(country) => updateForm({ country })}
                            />
                          </label>

                          <label>
                            Data prenotazione
                            <input
                              type="date"
                              value={form.bookingDate}
                              onChange={(e) => updateForm({ bookingDate: e.target.value })}
                            />
                          </label>
                        </div>
                      </div>
                    </details>

                    {/* ── Dati finanziari ───────────────────────── */}
                    <details className="prenotazioni-section" open>
                      <summary className="prenotazioni-section-summary">Dati finanziari</summary>
                      <div className="prenotazioni-section-body">
                        <div className="prenotazioni-grid">

                          <label>
                            Totale lordo (pagato dal locatario)
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.totalNet}
                              onChange={(e) => {
                                const lordo = Number(e.target.value);
                                const rate = Math.max(0, Number(estimatedTaxRate) || 0) / 100;
                                // L'imposta si ricalcola automaticamente al 21% del lordo
                                // (resta comunque modificabile a mano qui sotto).
                                updateForm({
                                  totalNet: lordo,
                                  taxPaid: roundTo2(Math.max(0, lordo) * rate),
                                });
                              }}
                            />
                          </label>

                          <label>
                            Totale netto (nostro incasso)
                            <div className="prenotazioni-inline-calc">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.totalGross}
                                onChange={(e) => updateForm({ totalGross: Number(e.target.value) })}
                              />
                              {form.totalGross === 0 && form.totalNet > 0 && form.commission > 0 ? (
                                <button
                                  type="button"
                                  className="admin-secondary-btn"
                                  onClick={handleCalculateNetFromLordoAndCommission}
                                >
                                  Calcola netto
                                </button>
                              ) : null}
                            </div>
                          </label>

                          <label>
                            Commissione piattaforma
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.commission}
                              onChange={(e) => updateForm({ commission: Number(e.target.value) })}
                            />
                          </label>

                          <label>
                            Imposta già pagata (auto 21% del lordo)
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.taxPaid}
                              onChange={(e) => updateForm({ taxPaid: Number(e.target.value) })}
                            />
                          </label>

                          <label>
                            Prezzo a notte
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.pricePerNight}
                              onChange={(e) => updateForm({ pricePerNight: Number(e.target.value) })}
                            />
                          </label>

                          <label>
                            Persona supp.
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={form.extraPerson}
                              onChange={(e) => updateForm({ extraPerson: Number(e.target.value) })}
                            />
                          </label>
                        </div>
                      </div>
                    </details>

                    {/* ── Letti ─────────────────────────────────── */}
                    <details className="prenotazioni-section">
                      <summary className="prenotazioni-section-summary">Letti</summary>
                      <div className="prenotazioni-section-body">
                        <div className="prenotazioni-checks">
                          <label className="prenotazioni-check">
                            <input
                              type="checkbox"
                              checked={Boolean(form.beds.matrimoniale)}
                              onChange={(e) => updateBeds('matrimoniale', e.target.checked)}
                            />
                            Letto matrimoniale
                          </label>
                          <label className="prenotazioni-check">
                            <input
                              type="checkbox"
                              checked={Boolean(form.beds.lettino1)}
                              onChange={(e) => updateBeds('lettino1', e.target.checked)}
                            />
                            Lettino 1
                          </label>
                          <label className="prenotazioni-check">
                            <input
                              type="checkbox"
                              checked={Boolean(form.beds.lettino2)}
                              onChange={(e) => updateBeds('lettino2', e.target.checked)}
                            />
                            Lettino 2
                          </label>
                          <label className="prenotazioni-check">
                            <input
                              type="checkbox"
                              checked={Boolean(form.beds.divanoLetto)}
                              onChange={(e) => updateBeds('divanoLetto', e.target.checked)}
                            />
                            Divano letto
                          </label>
                          <label className="prenotazioni-check">
                            <input
                              type="checkbox"
                              checked={Boolean(form.beds.lettiniAssemblati)}
                              onChange={(e) => updateBeds('lettiniAssemblati', e.target.checked)}
                            />
                            Lettini assemblati
                          </label>
                        </div>
                      </div>
                    </details>

                    {/* ── Note & Commenti ───────────────────────── */}
                    <details className="prenotazioni-section">
                      <summary className="prenotazioni-section-summary">Note & Commenti</summary>
                      <div className="prenotazioni-section-body">
                        <label className="prenotazioni-notes">
                          Osservazioni
                          <textarea
                            value={form.notes}
                            onChange={(e) => updateForm({ notes: e.target.value })}
                            rows={3}
                          />
                        </label>

                        <div className="prenotazioni-comments">
                          <div className="prenotazioni-comment-add">
                            <input
                              type="text"
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Aggiungi un commento…"
                            />
                            <button type="button" className="admin-secondary-btn" onClick={handleAddComment}>
                              Aggiungi
                            </button>
                          </div>

                          {Array.isArray(form.comments) && form.comments.length > 0 ? (
                            <ul className="prenotazioni-comment-list">
                              {form.comments.map((comment, index) => (
                                <li key={`${comment?.at || 'x'}-${index}`}>
                                  <span className="prenotazioni-comment-date">
                                    {comment?.at ? new Date(comment.at).toLocaleString('it-IT') : ''}
                                  </span>
                                  <span className="prenotazioni-comment-text">{comment?.text || ''}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="prenotazioni-empty">Nessun commento.</p>
                          )}
                        </div>
                      </div>
                    </details>

                    <div className="prenotazioni-actions">
                      <button type="button" className="admin-copy-btn" onClick={saveOverlay}>
                        Salva
                      </button>
                      <button type="button" className="admin-secondary-btn" onClick={deleteOverlay}>
                        {isManualSelected ? 'Elimina periodo' : 'Elimina dati'}
                      </button>
                      <button
                        type="button"
                        className="admin-secondary-btn prenotazioni-recap-btn"
                        onClick={handleSendRecap}
                        title="Invia riepilogo al webhook (senza nome né importi)"
                      >
                        📤 Invia riepilogo
                      </button>
                      {saveStatus && <span className="prenotazioni-save-status">{saveStatus}</span>}
                    </div>
                  </>
                )}
                </section>
              </div>

              <details className="prenotazioni-stats">
                <summary className="prenotazioni-stats-summary">Statistiche</summary>

                <div className="prenotazioni-filters">
                  <div className="prenotazioni-filters-grid">
                    <label>
                      Prenotazione da
                      <input type="date" value={bookingFrom} onChange={(e) => setBookingFrom(e.target.value)} />
                    </label>
                    <label>
                      Prenotazione a
                      <input type="date" value={bookingTo} onChange={(e) => setBookingTo(e.target.value)} />
                    </label>
                    <label>
                      Sito
                      <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
                        <option value="all">Tutti</option>
                        <option value="airbnb">Airbnb</option>
                        <option value="booking">Booking</option>
                      </select>
                    </label>
                  </div>

                  <div className="prenotazioni-filters-actions">
                    <button
                      type="button"
                      className="admin-secondary-btn"
                      onClick={() => {
                        setBookingFrom('');
                        setBookingTo('');
                      }}
                    >
                      Reset
                    </button>
                    <span className="prenotazioni-filters-hint">
                      Periodi filtrati: {statsItems.length}
                    </span>
                    <label className="prenotazioni-tax-rate-input">
                      Aliquota fiscale stimata (%)
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={estimatedTaxRate}
                        onChange={(e) => setEstimatedTaxRate(Number(e.target.value))}
                      />
                    </label>
                    <label className="prenotazioni-tax-rate-input">
                      Costo menage (EUR)
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={cleaningCost}
                        onChange={(e) => setCleaningCost(Number(e.target.value))}
                      />
                    </label>
                  </div>
                </div>

                <div className="prenotazioni-kpis">
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Prenotazioni</span>
                    <span className="prenotazioni-kpi-value">{numberFormatter.format(statsKpis.reservations)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Notti</span>
                    <span className="prenotazioni-kpi-value">{numberFormatter.format(statsKpis.nights)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Totale lordo</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalLordo)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Incassato (netto saisi)</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalNetto)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Commissioni totali</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalCommission)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Taux commission moyen</span>
                    <span className="prenotazioni-kpi-value">
                      {typeof statsKpis.averageCommissionRate === 'number'
                        ? percentFormatter.format(statsKpis.averageCommissionRate)
                        : '—'}
                    </span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Impôt déjà payé</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalTaxPaid)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Impôt estimé total</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalEstimatedTax)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Impôt restant à payer</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalTaxRemaining)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Gain réel cumulé</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalRealIncome)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Dépense ménage</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalCleaningExpense)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Gain après ménage</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.totalRealIncomeAfterCleaning)}</span>
                  </div>
                  <div className="prenotazioni-kpi">
                    <span className="prenotazioni-kpi-label">Prezzo medio / notte</span>
                    <span className="prenotazioni-kpi-value">{currencyFormatter.format(statsKpis.avgPricePerNight)}</span>
                  </div>
                </div>

                <div className="prenotazioni-detail-table">
                  <h4>Détail des réservations</h4>
                  <table className="prenotazioni-table">
                    <thead>
                      <tr>
                        <th>Periodo</th>
                        <th>Sito</th>
                        <th>Ospite</th>
                        <th>Notti</th>
                        <th>Lordo</th>
                        <th>Commissione</th>
                        <th>Taux comm.</th>
                        <th>Netto</th>
                        <th>€/notte (net)</th>
                        <th>Imposta</th>
                        <th>Gain réel</th>
                        <th>Ménage</th>
                        <th>Gain après ménage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsItems.map((item) => {
                        const overlay = overlays[item.key] || {};
                        const lordo = item.totalNet;
                        const netto = item.totalGross;
                        const commission = item.commission;
                        const taxPaid = item.taxPaid;
                        const realIncome = item.realIncome;
                        const cleaningExpense = Math.max(0, Number(cleaningCost) || 0);
                        const realIncomeAfterCleaning = realIncome - cleaningExpense;
                        const netPerNight = netto > 0 && item.nights > 0 ? netto / item.nights : 0;
                        return (
                          <tr
                            key={item.key}
                            className="prenotazioni-table-row-clickable"
                            onClick={() => {
                              const selected = requestSelectKey(item.key);
                              if (selected) {
                                scrollToEditor();
                              }
                            }}
                          >
                            <td className="prenotazioni-td-period">{`${formatDate(item.start)} → ${formatDate(item.end)}`}</td>
                            <td><span className={`prenotazioni-badge prenotazioni-badge--${item.bookingMode || 'unknown'}`}>{bookingModeLabelForValue(item.bookingMode)}</span></td>
                            <td>{(overlay.name || '').trim() || '—'}</td>
                            <td className="prenotazioni-td-num">{item.nights}</td>
                            <td className="prenotazioni-td-money">{lordo > 0 ? currencyFormatter.format(lordo) : '—'}</td>
                            <td className="prenotazioni-td-money">{commission > 0 ? currencyFormatter.format(commission) : '—'}</td>
                            <td
                              className="prenotazioni-td-rate"
                              data-rate={
                                typeof item.commissionRate === 'number'
                                  ? item.commissionRate > 0.18 ? 'high' : item.commissionRate > 0.1 ? 'mid' : 'low'
                                  : 'none'
                              }
                            >
                              {typeof item.commissionRate === 'number' ? percentFormatter.format(item.commissionRate) : '—'}
                            </td>
                            <td className="prenotazioni-td-netto">{netto > 0 ? currencyFormatter.format(netto) : '—'}</td>
                            <td className="prenotazioni-td-money">{netPerNight > 0 ? currencyFormatter.format(netPerNight) : '—'}</td>
                            <td className="prenotazioni-td-money">{taxPaid > 0 ? currencyFormatter.format(taxPaid) : '—'}</td>
                            <td className="prenotazioni-td-real">{realIncome > 0 ? currencyFormatter.format(realIncome) : '—'}</td>
                            <td className="prenotazioni-td-money">{currencyFormatter.format(cleaningExpense)}</td>
                            <td className="prenotazioni-td-real">{currencyFormatter.format(realIncomeAfterCleaning)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan={4}>Total</th>
                        <th>{currencyFormatter.format(statsKpis.totalLordo)}</th>
                        <th>{currencyFormatter.format(statsKpis.totalCommission)}</th>
                        <th>{typeof statsKpis.averageCommissionRate === 'number' ? percentFormatter.format(statsKpis.averageCommissionRate) : '—'}</th>
                        <th>{currencyFormatter.format(statsKpis.totalNetto)}</th>
                        <th>{statsKpis.nights > 0 && statsKpis.totalNetto > 0 ? currencyFormatter.format(statsKpis.totalNetto / statsKpis.nights) : '—'}</th>
                        <th>{currencyFormatter.format(statsKpis.totalTaxPaid)}</th>
                        <th>{currencyFormatter.format(statsKpis.totalRealIncome)}</th>
                        <th>{currencyFormatter.format(statsKpis.totalCleaningExpense)}</th>
                        <th>{currencyFormatter.format(statsKpis.totalRealIncomeAfterCleaning)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="prenotazioni-charts">
                  {/* 1. CA net réalisé par mois + bénéfice net final (vert) */}
                  <div className="prenotazioni-chart">
                    <h4>Fatturato netto per mese</h4>
                    <p className="prenotazioni-chart-hint">
                      Barre : incasso netto. Linea verde : guadagno netto dopo commissione, imposta e pulizie.
                    </p>
                    <div className="prenotazioni-chart-inner">
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={monthlyChartData} margin={{ top: 10, right: 18, bottom: 10, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis tickFormatter={(v) => `${Math.round(v)}€`} />
                          <Tooltip formatter={(value) => currencyFormatter.format(Number(value) || 0)} />
                          <Legend />
                          <Bar dataKey="totalGross" name="Fatturato netto" fill="var(--accent)" />
                          <Line type="monotone" dataKey="netProfit" name="Guadagno netto finale" stroke={CHART_GREEN} strokeWidth={2.5} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 2. CA net réalisé par semaine + bénéfice net final (vert) */}
                  <div className="prenotazioni-chart">
                    <h4>Fatturato netto per settimana</h4>
                    <p className="prenotazioni-chart-hint">
                      Asse X : lunedì della settimana. Linea verde : guadagno netto finale.
                    </p>
                    <div className="prenotazioni-chart-inner">
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={weeklyChartData} margin={{ top: 10, right: 18, bottom: 10, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="week" />
                          <YAxis tickFormatter={(v) => `${Math.round(v)}€`} />
                          <Tooltip formatter={(value) => currencyFormatter.format(Number(value) || 0)} />
                          <Legend />
                          <Bar dataKey="totalGross" name="Fatturato netto" fill="var(--accent)" />
                          <Line type="monotone" dataKey="netProfit" name="Guadagno netto finale" stroke={CHART_GREEN} strokeWidth={2.5} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 3. Taux de remplissage (nuits) par mois */}
                  <div className="prenotazioni-chart">
                    <h4>Tasso di riempimento (notti) per mese</h4>
                    <p className="prenotazioni-chart-hint">
                      Notti prenotate / notti del mese.
                    </p>
                    <div className="prenotazioni-chart-inner">
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={occupancyByMonth} margin={{ top: 10, right: 18, bottom: 10, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === 'Riempimento') {
                                return percentFormatter.format(Number(value) || 0);
                              }
                              return numberFormatter.format(Number(value) || 0);
                            }}
                          />
                          <Legend />
                          <Bar dataKey="occupancy" name="Riempimento" fill="var(--accent-hover)" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 4. Formulaire : détail d'un mois choisi */}
                  <div className="prenotazioni-chart prenotazioni-chart--full">
                    <h4>Dettaglio di un mese</h4>
                    <form
                      className="prenotazioni-month-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        setSelectedStayMonth(monthDetailChoice || null);
                      }}
                    >
                      <label>
                        Mese del soggiorno
                        <select
                          value={monthDetailChoice}
                          onChange={(e) => setMonthDetailChoice(e.target.value)}
                        >
                          <option value="">— Scegli un mese —</option>
                          {monthlyData.map((month) => (
                            <option key={month.monthKey} value={month.monthKey}>{month.month}</option>
                          ))}
                        </select>
                      </label>
                      <button type="submit" className="admin-copy-btn" disabled={!monthDetailChoice}>
                        Vedi dettaglio
                      </button>
                      {selectedStayMonth && (
                        <button
                          type="button"
                          className="admin-secondary-btn"
                          onClick={() => {
                            setSelectedStayMonth(null);
                            setMonthDetailChoice('');
                          }}
                        >
                          Reset
                        </button>
                      )}
                    </form>

                    {selectedStayMonth && selectedStayMonthSummary && (
                      <>
                        <div className="prenotazioni-month-summary">
                          <div className="prenotazioni-kpi">
                            <span className="prenotazioni-kpi-label">Prenotazioni</span>
                            <span className="prenotazioni-kpi-value">{numberFormatter.format(selectedStayMonthSummary.reservations)}</span>
                          </div>
                          <div className="prenotazioni-kpi">
                            <span className="prenotazioni-kpi-label">Notti</span>
                            <span className="prenotazioni-kpi-value">{numberFormatter.format(selectedStayMonthSummary.nights)}</span>
                          </div>
                          <div className="prenotazioni-kpi">
                            <span className="prenotazioni-kpi-label">Fatturato netto</span>
                            <span className="prenotazioni-kpi-value">{currencyFormatter.format(selectedStayMonthSummary.totalGross)}</span>
                          </div>
                          <div className="prenotazioni-kpi">
                            <span className="prenotazioni-kpi-label">Fatturato lordo</span>
                            <span className="prenotazioni-kpi-value">{currencyFormatter.format(selectedStayMonthSummary.totalNet)}</span>
                          </div>
                        </div>

                        <p className="prenotazioni-chart-hint">
                          Quando sono state prenotate le notti di {monthLabelForKey(selectedStayMonth)} :
                        </p>

                        {stayMonthBookingBreakdown.length === 0 ? (
                          <p className="prenotazioni-empty">Nessuna prenotazione per questo mese.</p>
                        ) : (
                          <div className="prenotazioni-chart-inner">
                            <ResponsiveContainer width="100%" height={280}>
                              <ComposedChart data={stayMonthBookingBreakdown} margin={{ top: 10, right: 18, bottom: 10, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" />
                                <YAxis yAxisId="left" allowDecimals={false} />
                                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(v)}€`} />
                                <Tooltip
                                  formatter={(value, name) => {
                                    if (name === 'Fatturato netto') {
                                      return currencyFormatter.format(Number(value) || 0);
                                    }
                                    return numberFormatter.format(Number(value) || 0);
                                  }}
                                />
                                <Legend />
                                <Bar yAxisId="left" dataKey="reservations" name="Prenotazioni" fill="var(--accent-hover)" />
                                <Line yAxisId="right" type="monotone" dataKey="totalGross" name="Fatturato netto" stroke="var(--brand-airbnb)" strokeWidth={2} dot={false} />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* 5. Camembert : nombre de réservations par plateforme */}
                  <div className="prenotazioni-chart">
                    <h4>Prenotazioni per piattaforma (numero)</h4>
                    <div className="prenotazioni-chart-inner">
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Tooltip formatter={(v) => numberFormatter.format(Number(v) || 0)} />
                          <Legend />
                          <Pie data={bookingModeData} dataKey="value" nameKey="name" outerRadius={100} label={renderPiePercent}>
                            {bookingModeData.map((entry) => (
                              <Cell key={entry.name} fill={bookingModeColor(entry.name)} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 6. Camembert : CA net par plateforme */}
                  <div className="prenotazioni-chart">
                    <h4>Fatturato netto per piattaforma</h4>
                    <div className="prenotazioni-chart-inner">
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Tooltip formatter={(v) => currencyFormatter.format(Number(v) || 0)} />
                          <Legend />
                          <Pie data={bookingModeNetPie} dataKey="value" nameKey="name" outerRadius={100} label={renderPiePercent}>
                            {bookingModeNetPie.map((entry) => (
                              <Cell key={entry.name} fill={bookingModeColor(entry.name)} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Pays (conservé) */}
                  <div className="prenotazioni-chart">
                    <h4>Paesi</h4>
                    <div className="prenotazioni-chart-inner">
                      <ResponsiveContainer width="100%" height={280}>
                        <ComposedChart data={countryData} layout="vertical" margin={{ top: 10, right: 18, bottom: 10, left: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={120} />
                          <Tooltip formatter={(v) => numberFormatter.format(Number(v) || 0)} />
                          <Bar dataKey="value" name="Prenotazioni" fill="var(--accent-hover)" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </details>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default GestionePrenotazioni;
